import { Stack } from "@mui/material";
import { NavigationTabs } from "components/NavigationTabs";
import { DynamicRecordDrawer } from "features/shared/components/dynamic-form";
import { CatalogTablePanel } from "features/shared/components/technical-config-tab/CatalogTablePanel";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetShadingSystemCatalog, useGetWindowTypeCatalog } from "../api/hooks";
import { dedupeShadingCatalogForPoolDisplay } from "../lib/windowCatalogMappers";
import { getShadingTypePoolTableHeaders, getWindowTypePoolTableHeaders } from "../lib/windowCatalogTableHeaders";
import { WINDOW_CATALOG_POOL_TABS, type WindowCatalogPoolTab } from "../lib/windowConfigFieldDefinitions";

type WindowCatalogDrawerProps = {
  onClose: () => void;
  open: boolean;
};

export const WindowCatalogDrawer = ({ onClose, open }: WindowCatalogDrawerProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<WindowCatalogPoolTab>(WINDOW_CATALOG_POOL_TABS.windowTypes);
  const {
    data: windowCatalog = [],
    isError: windowCatalogError,
    isLoading: windowCatalogLoading,
  } = useGetWindowTypeCatalog(open);
  const {
    data: shadingCatalog = [],
    isError: shadingCatalogError,
    isLoading: shadingCatalogLoading,
  } = useGetShadingSystemCatalog(undefined, open);

  const navigationTabs = useMemo(
    () => [
      {
        displayName: String(t("TechnicalData.windows.poolTabs.windowTypes")),
        name: WINDOW_CATALOG_POOL_TABS.windowTypes,
      },
      {
        displayName: String(t("TechnicalData.windows.poolTabs.shadingTypes")),
        name: WINDOW_CATALOG_POOL_TABS.shadingTypes,
      },
    ],
    [t]
  );

  const handleTabChange = (tab: { name: string }) => {
    if (tab.name === WINDOW_CATALOG_POOL_TABS.windowTypes) {
      setActiveTab(WINDOW_CATALOG_POOL_TABS.windowTypes);
      return;
    }
    if (tab.name === WINDOW_CATALOG_POOL_TABS.shadingTypes) {
      setActiveTab(WINDOW_CATALOG_POOL_TABS.shadingTypes);
    }
  };

  const windowHeaders = useMemo(() => getWindowTypePoolTableHeaders(t), [t]);
  const shadingHeaders = useMemo(() => getShadingTypePoolTableHeaders(t), [t]);
  const uniqueShadingCatalog = useMemo(
    () => dedupeShadingCatalogForPoolDisplay(shadingCatalog, shadingHeaders),
    [shadingCatalog, shadingHeaders]
  );
  const activeNavigationTab = navigationTabs.find((tab) => tab.name === activeTab) ?? navigationTabs[0];

  const renderTabContent = () => {
    if (activeTab === WINDOW_CATALOG_POOL_TABS.windowTypes) {
      return (
        <CatalogTablePanel
          data={windowCatalog}
          headers={windowHeaders}
          idColumnName="id"
          isLoading={windowCatalogLoading}
        />
      );
    }

    return (
      <CatalogTablePanel
        data={uniqueShadingCatalog}
        headers={shadingHeaders}
        idColumnName="id"
        isLoading={shadingCatalogLoading}
      />
    );
  };

  const catalogErrorMessage =
    (windowCatalogError && activeTab === WINDOW_CATALOG_POOL_TABS.windowTypes) ||
    (shadingCatalogError && activeTab === WINDOW_CATALOG_POOL_TABS.shadingTypes)
      ? String(t("TechnicalData.windows.catalogLoadError"))
      : null;

  return (
    <DynamicRecordDrawer
      hideFooter
      onClose={onClose}
      open={open}
      title={String(t("TechnicalData.windows.catalogTitle"))}
      widthClassName="w-[110rem]"
    >
      <Stack className="h-full w-full flex-col items-stretch gap-4">
        <NavigationTabs activeTab={activeNavigationTab} setActiveTab={handleTabChange} tabs={navigationTabs} />
        {catalogErrorMessage && (
          <p className="font-ds-body-regular-sm text-lds-purpose-status-negative-subtle">{catalogErrorMessage}</p>
        )}
        <Stack className="w-full flex-1 flex-col overflow-hidden">{renderTabContent()}</Stack>
      </Stack>
    </DynamicRecordDrawer>
  );
};
